/**
 * Discovery Portal (V1) — token-gated public page.
 *
 * Route surface:
 *   - Legacy: ``/discovery/?t=<token>`` (still supported for any
 *     links already sent before the slug rollout).
 *   - Personalised bootstrap: ``/discovery/<slug>?t=<token>`` — on
 *     first open we swap the token for an HttpOnly cookie by POSTing
 *     ``/api/public/discovery/session``, then rewrite the address
 *     bar to the clean ``/discovery/<slug>`` URL. The prospect never
 *     sees the token again.
 *   - Personalised return visit: ``/discovery/<slug>`` — no token,
 *     no query-string; access is granted purely by the same-origin
 *     ``dp_<slug>`` cookie that was set on first visit.
 *
 * The page NEVER shows any real information without a valid session.
 * A missing/bad token / cookie renders a friendly "not found" so
 * the endpoint doesn't leak which prospects exist.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import TerritoryMap from "@/components/territory/TerritoryMap";
import PortalPdfViewer from "@/components/pdf/PortalPdfViewer";
import { FileText, Download, Loader2, MapPin, Calendar, Phone, Mail, Home as HomeIcon, Map as MapIcon, Layers, GraduationCap, Search, CheckCircle2, Info, ChevronDown, ChevronUp } from "lucide-react";
import LaunchPortalOverlay, {
  LaunchPortalMobileNavStrip,
  useLaunchPortalSections,
} from "@/components/launch-portal/LaunchPortalOverlay";
import { DbsDirtyGate, useDbsDirtyGate } from "@/components/launch-portal/DbsDirtyGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/brand/creative-mojo-logo.png";

// Ordered nav list — drives both the desktop sidebar and the mobile
// top strip.  ``HOME`` is a virtual entry that scrolls the whole
// page back to the top (no anchor id — the tracker treats it as
// active when the viewport is at the very top).
const NAV_SECTIONS = [
  {
    id: "__home__",           // sentinel — no matching <section> anchor
    label: "Home",
    shortLabel: "Home",
    icon: HomeIcon,
    tone: {
      text: "text-emerald-600",
      bg: "bg-emerald-50",
      ring: "ring-emerald-200",
      activeBg: "bg-emerald-100",
      activeRing: "ring-emerald-500",
    },
  },
  {
    id: "territory",
    label: "Your Proposed Territory",
    shortLabel: "Your Proposed Territory",
    icon: MapPin,
    tone: {
      text: "text-teal-600",
      bg: "bg-teal-50",
      ring: "ring-teal-200",
      activeBg: "bg-teal-100",
      activeRing: "ring-teal-500",
    },
  },
  {
    id: "agreement",
    label: "Main Franchise Agreement",
    shortLabel: "Main Franchise Agreement",
    icon: FileText,
    tone: {
      text: "text-violet-600",
      bg: "bg-violet-50",
      ring: "ring-violet-200",
      activeBg: "bg-violet-100",
      activeRing: "ring-violet-500",
    },
  },
  {
    id: "confidentiality",
    label: "Confidentiality Agreement",
    shortLabel: "Confidentiality",
    icon: FileText,
    tone: {
      text: "text-amber-600",
      bg: "bg-amber-50",
      ring: "ring-amber-200",
      activeBg: "bg-amber-100",
      activeRing: "ring-amber-500",
    },
  },
  {
    id: "shadow",
    label: "Shadow Day Dates",
    shortLabel: "Shadow Day Dates",
    icon: Calendar,
    tone: {
      text: "text-orange-500",
      bg: "bg-orange-50",
      ring: "ring-orange-200",
      activeBg: "bg-orange-100",
      activeRing: "ring-orange-500",
    },
  },
  {
    id: "training",
    label: "Training Day Dates",
    shortLabel: "Training Day Dates",
    icon: GraduationCap,
    tone: {
      text: "text-rose-500",
      bg: "bg-rose-50",
      ring: "ring-rose-200",
      activeBg: "bg-rose-100",
      activeRing: "ring-rose-500",
    },
  },
];

// pdfjs worker — mirrors the ContractPdfViewer setup so the Discovery
// Portal renders PDFs identically across Chrome / Safari / Firefox.
// Chrome's built-in PDF plugin can be flaky when handed a same-origin
// streaming endpoint inside an ``<iframe>`` (it sometimes falls back
// to the "download this file" tile shown in the reported bug). Using
// pdfjs guarantees the pages are painted onto a canvas by our own
// JS — no browser plugin heuristics involved.
const PDF_WORKER_URL = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;
if (typeof window !== "undefined" && !pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;
}

// Same-origin axios instance so the ``dp_<slug>`` HttpOnly cookie
// is sent on subsequent requests without extra ceremony at each
// call-site.
const publicApi = axios.create({ baseURL: API, withCredentials: true });


// -----------------------------------------------------------------
// Small primitives — kept inside the file so V1 stays self-contained.
// -----------------------------------------------------------------
function SectionHeading({ children, testId, icon: Icon, tone }) {
  return (
    <h2
      data-testid={testId}
      className="text-lg sm:text-xl font-bold uppercase tracking-wider text-stone-900 border-b-2 border-[#dddd16] pb-2 mb-5 flex items-center gap-3"
    >
      {Icon && (
        <span
          className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${tone?.bg || "bg-stone-100"} ${tone?.text || "text-stone-600"} shrink-0`}
          aria-hidden="true"
        >
          <Icon className="w-5 h-5" />
        </span>
      )}
      <span>{children}</span>
    </h2>
  );
}

function Section({ id, heading, testId, icon, tone, children, showMobileBanner = false, bannerTestId }) {
  return (
    <section
      id={id}
      className="mt-12 scroll-mt-52 md:scroll-mt-28"
      data-testid={testId}
    >
      <SectionHeading testId={`${testId}-heading`} icon={icon} tone={tone}>
        {heading}
      </SectionHeading>
      {showMobileBanner && (
        <MobileViewingAdviceBanner testId={bannerTestId || `${testId}-viewing-advice`} />
      )}
      {children}
    </section>
  );
}

function Placeholder({ text, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg border border-dashed border-stone-300 bg-stone-50 text-stone-500 py-10 text-center text-sm uppercase tracking-wider font-bold"
    >
      {text}
    </div>
  );
}


// -----------------------------------------------------------------
// Territory map — reuses the existing admin ``TerritoryMap``
// component so styling matches the ``/share/…`` public territory
// viewer. Home markers are suppressed per V1 brief.
// -----------------------------------------------------------------
function TerritoryPanel({ territory }) {
  // Basemap toggle — mirrors the ``/share/territory/:token`` public
  // viewer. Light is the default for clean contrast; Roads adds the
  // street network so prospects can see how the area connects.
  const [basemap, setBasemap] = useState("light");

  const mapSectors = (territory?.sectors || []).map((s) => ({
    sector: s.sector,
    geometry: s.geometry,
    home_count: s.home_count || 0,
    distance_km: 0,
  }));
  const selectedCodes = (territory?.sector_codes || []).slice();
  const centre = territory?.centre || null;
  const homeCount =
    typeof territory?.home_count === "number"
      ? territory.home_count
      : mapSectors.reduce((n, s) => n + (s.home_count || 0), 0);

  return (
    <div className="space-y-5" data-testid="discovery-territory-map">
      {/* Panel header — mirrors the share page's "Care Homes" chip so
          the total home count is visible at the top of the section. */}
      <div className="bg-white border border-stone-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-stone-600">
          <MapPin className="w-4 h-4" />
          <span className="font-bold text-stone-800" data-testid="discovery-territory-name">
            {territory?.name || "Proposed Territory"}
          </span>
        </div>
        <div className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-2 flex items-center gap-3">
          <HomeIcon className="w-5 h-5 text-emerald-600" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">
              Care Homes
            </div>
            <div className="font-display text-2xl text-stone-950 tabular-nums" data-testid="discovery-home-count">
              {homeCount}
            </div>
          </div>
        </div>
      </div>

      {/* Map with overlaid LIGHT/ROADS basemap toggle (identical to
          the public share page). */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden relative">
        <div
          className="absolute top-3 left-3 z-10 bg-white border border-stone-200 rounded-lg shadow-sm flex p-0.5"
          data-testid="discovery-basemap-toggle"
        >
          <button
            onClick={() => setBasemap("light")}
            data-testid="discovery-basemap-light"
            className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded flex items-center gap-1 transition-colors ${
              basemap === "light"
                ? "bg-stone-950 text-white"
                : "text-stone-700 hover:bg-stone-100"
            }`}
            title="Clean basemap (best for territory contrast)"
          >
            <Layers className="w-3 h-3" /> Light
          </button>
          <button
            onClick={() => setBasemap("streets")}
            data-testid="discovery-basemap-streets"
            className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded flex items-center gap-1 transition-colors ${
              basemap === "streets"
                ? "bg-stone-950 text-white"
                : "text-stone-700 hover:bg-stone-100"
            }`}
            title="Show roads, motorways and major routes"
          >
            <MapIcon className="w-3 h-3" /> Roads
          </button>
        </div>
        <TerritoryMap
          sectors={mapSectors}
          selected={selectedCodes}
          centre={centre}
          centreLabel={territory?.centre_postcode || ""}
          height={820}
          interactive={false}
          basemap={basemap}
        />
      </div>

      {/* Below-map info — "At a glance" + "Postcode sectors in this
          territory" cards, styled to match the public share page. */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="discovery-territory-at-a-glance">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500 mb-2">
            At a glance
          </div>
          <ul className="text-sm text-stone-700 space-y-1.5 leading-relaxed">
            <li className="flex items-baseline gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
              <span>
                <strong className="text-stone-950 tabular-nums">{selectedCodes.length}</strong> postcode sectors
              </span>
            </li>
            <li className="flex items-baseline gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
              <span>
                <strong className="text-stone-950 tabular-nums">{homeCount}</strong> regulated care homes (CQC-registered)
              </span>
            </li>
            {territory?.centre_postcode && (
              <li className="flex items-baseline gap-2">
                <MapPin className="w-3.5 h-3.5 text-stone-500 shrink-0 mt-0.5" />
                <span>
                  Centred near <strong className="text-stone-950">{territory.centre_postcode}</strong>
                </span>
              </li>
            )}
          </ul>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="discovery-territory-sectors">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500 mb-2">
            Postcode sectors in this territory
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-auto">
            {selectedCodes.map((s) => (
              <span key={s} className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-stone-100 text-stone-700 rounded">
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------
// Franchise Agreement viewer — the PDF rendering machinery has been
// extracted into ``@/components/pdf/PortalPdfViewer`` so the Launch
// Portal can call it too. See AgreementPanel below for the wrapper.
// -----------------------------------------------------------------

// -----------------------------------------------------------------
// Franchise Agreement viewer + download.
//
// The PDF machinery has been extracted into ``PortalPdfViewer`` so
// the Launch Portal can call it too — but the DOM, styling, modal,
// loading-state and z-index are byte-identical to the historic
// AgreementPanel. See ``components/pdf/PortalPdfViewer.jsx``.
//
// When Sam ticks ``SHOW FLEX DOCUMENTS`` on the Sales Pipeline
// checklist for this contact, two additional ``PortalPdfViewer``
// instances render underneath the main agreement — one for the Flex
// Payment Structure PDF, one for the Flex Addendum Agreement. Both
// reuse the exact same viewer with different URLs and titles.
// -----------------------------------------------------------------
function AgreementPanel({ authQuery, showFlex }) {
  const inlineUrl = `${API}/public/discovery/agreement.pdf?${authQuery}`;
  const downloadUrl = `${API}/public/discovery/agreement.pdf/download?${authQuery}`;
  const flexStructureInline = `${API}/public/discovery/flex/structure.pdf?${authQuery}`;
  const flexStructureDownload = `${API}/public/discovery/flex/structure.pdf/download?${authQuery}`;
  const flexAddendumInline = `${API}/public/discovery/flex/addendum.pdf?${authQuery}`;
  const flexAddendumDownload = `${API}/public/discovery/flex/addendum.pdf/download?${authQuery}`;

  return (
    <div>
      <p className="text-stone-700 leading-relaxed mb-6 max-w-3xl">
        Our Franchise Agreement is a detailed document, as it needs to clearly set out how the franchise works,
        what you can expect from Creative Mojo and what we ask from you in return. We appreciate that it can
        look a little daunting at first, but much of it is there simply to make sure everything is clear and
        properly covered for both sides. Please take your time reading through it, and if there is anything
        you are unsure about, Paul is very happy to go through any questions with you and explain any part of
        the agreement in more detail.
      </p>

      <PortalPdfViewer
        inlineUrl={inlineUrl}
        downloadUrl={downloadUrl}
        viewButtonLabel="View Agreement"
        downloadButtonLabel="Download a Local Copy"
        modalTitle="Main Franchise Agreement (Sample)"
        testidPrefix="discovery-agreement"
      />

      {showFlex && (
        <div className="mt-12 space-y-12" data-testid="discovery-flex-block">
          <PortalPdfViewer
            title="Flex Payment Structure"
            intro="Our Flex payment structure offers an alternative way to spread the initial cost of starting your Creative Mojo franchise. This overview explains how the payment structure works and the key stages involved."
            inlineUrl={flexStructureInline}
            downloadUrl={flexStructureDownload}
            viewButtonLabel="View Flex Payment Structure"
            downloadButtonLabel="Download a Local Copy"
            modalTitle="Flex Payment Structure"
            testidPrefix="discovery-flex-structure"
          />
          <PortalPdfViewer
            title="Flex Addendum Agreement"
            intro="If you choose the Flex payment option, a short addendum is used alongside your main Franchise Agreement to record the additional payment terms. This sample document shows how the Flex arrangement forms part of the overall agreement."
            inlineUrl={flexAddendumInline}
            downloadUrl={flexAddendumDownload}
            viewButtonLabel="View Flex Addendum Agreement"
            downloadButtonLabel="Download a Local Copy"
            modalTitle="Flex Addendum Agreement"
            testidPrefix="discovery-flex-addendum"
          />
        </div>
      )}
    </div>
  );
}



// -----------------------------------------------------------------
// Confidentiality Agreement — activated by the admin ticking Shadow
// Day AND entering a Shadow Day date on the contact record. Renders
// the branded wording snapshot from the backend, plus a signing
// form. On successful submit the payload flips to `status: signed`
// via `reload()` and the same panel re-renders in read-only mode.
// -----------------------------------------------------------------
function ConfidentialityAgreementPanel({ data, slug, reload }) {
  const [signature, setSignature] = useState("");
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Client-only flag that flips to true the moment a successful
  // submit lands on THIS device / session. Drives the celebratory
  // "Thank you for signing" banner above the signed block. Never
  // persisted — a returning visitor with an already-signed record
  // sees the read-only block without the banner, which is what we
  // want (the banner is a submission acknowledgement, not a status).
  const [justSigned, setJustSigned] = useState(false);
  if (!data || data.activated === false) {
    return (
      <div className="max-w-3xl">
        <div className="rounded-xl border border-stone-200 bg-white p-6 text-sm text-stone-600" data-testid="discovery-confidentiality-not-activated">
          The Confidentiality Agreement will appear here once your Shadow Day has been confirmed with a scheduled date.
        </div>
      </div>
    );
  }
  const signed = data.status === "signed";
  const fullName = data.full_name || data.fao_name || "";
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!ack) { setError("Please tick the acknowledgement checkbox."); return; }
    if (!signature.trim()) { setError("A typed signature is required."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/public/discovery/${encodeURIComponent(slug)}/confidentiality-agreement`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ typed_signature: signature, acknowledged: true, full_name: fullName }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || "Submission failed");
      // Flip the local flag BEFORE reloading so the banner is
      // already primed when the fresh payload arrives.
      setJustSigned(true);
      // Silent reload keeps the parent layout mounted (no spinner
      // remount), so the visitor stays on the Confidentiality tab
      // instead of being bounced back to Home.
      if (typeof reload === "function") await reload({ silent: true });
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="max-w-3xl" data-testid="discovery-confidentiality-panel">
      <div className="rounded-xl border border-stone-200 bg-white p-6 sm:p-8 shadow-sm">
        <div dangerouslySetInnerHTML={{ __html: data.wording_html || "" }} data-testid="discovery-confidentiality-wording" />
        {signed ? (
          <div className="mt-8 border-t border-stone-200 pt-6 text-sm text-stone-700" data-testid="discovery-confidentiality-signed-block">
            {justSigned && (
              <div
                className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
                data-testid="discovery-confidentiality-thankyou"
                role="status"
                aria-live="polite"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-emerald-900">
                  <p className="font-bold mb-1">Thank you for signing.</p>
                  <p className="text-emerald-800 leading-relaxed">
                    We&apos;ve received your Confidentiality Agreement and a copy has been recorded against your Discovery Portal. Paul and Sandra will be in touch shortly to confirm the next steps.
                  </p>
                </div>
              </div>
            )}
            <p className="mb-2"><strong>I/We acknowledge receipt and agree to the terms of this letter agreement.</strong></p>
            <p><strong>Signature:</strong> <span className="italic">{data.typed_signature}</span></p>
            <p><strong>Full Name:</strong> {data.full_name}</p>
            <p className="text-xs text-stone-500 mt-2">Submitted {data.submitted_at ? new Date(data.submitted_at).toLocaleString("en-GB") : ""}. This record is read-only.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 border-t border-stone-200 pt-6 space-y-4" data-testid="discovery-confidentiality-form">
            <label className="flex items-start gap-2 text-sm text-stone-800">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" data-testid="discovery-confidentiality-ack" required />
              <span>I acknowledge receipt and agree to the terms of this agreement.</span>
            </label>
            <div>
              <label className="block text-xs uppercase font-bold tracking-wider text-stone-600 mb-1">Typed Signature *</label>
              <input type="text" value={signature} onChange={(e) => setSignature(e.target.value)} required data-testid="discovery-confidentiality-signature" className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="Type your name to sign" />
            </div>
            <div>
              <label className="block text-xs uppercase font-bold tracking-wider text-stone-600 mb-1">Full Name</label>
              <input type="text" value={fullName} readOnly data-testid="discovery-confidentiality-fullname" className="w-full px-3 py-2 border border-stone-200 bg-stone-50 rounded-lg text-sm text-stone-700" />
            </div>
            {error && <div className="text-sm text-red-700" data-testid="discovery-confidentiality-error">{error}</div>}
            <button type="submit" disabled={submitting} data-testid="discovery-confidentiality-submit" className="px-4 py-2 bg-stone-950 text-white rounded-lg text-xs font-bold uppercase tracking-widest disabled:opacity-50">
              {submitting ? "Submitting…" : "Submit Agreement"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}



// -----------------------------------------------------------------
// Shadow Day panel.
// -----------------------------------------------------------------
function ShadowDayPanel({ shadow_day }) {
  const s = shadow_day || { status: "pending" };
  const dateFormatted = useMemo(() => {
    if (!s.date) return "";
    try {
      const d = new Date(s.date);
      return d.toLocaleDateString("en-GB", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    } catch { return s.date; }
  }, [s.date]);

  return (
    <div>
      <p className="text-stone-700 leading-relaxed mb-6 max-w-3xl">
        One of the most valuable parts of discovering whether Creative Mojo is right for you is spending time
        with us on a shadow day. Wherever possible, we arrange for you to join one of our experienced regional
        franchisees so you can see a typical working day firsthand. If that isn&apos;t practical, you&apos;re very welcome
        to visit us at our Devon headquarters instead. It&apos;s a relaxed opportunity to ask questions, meet the
        team and experience the business before making any decisions. If, afterwards, you feel Creative Mojo
        is the right fit for you, we&apos;ll then arrange your franchise training and begin preparing for your launch.
      </p>

      {s.status === "pending" && (
        <Placeholder text="Awaiting Shadow Day Appointment" testId="discovery-shadow-pending" />
      )}

      {s.status === "hq" && (
        <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm max-w-xl" data-testid="discovery-shadow-hq">
          <ShadowRow icon={Calendar} label="Shadow Day Date" value={dateFormatted} />
          <ShadowRow icon={MapPin} label="Location" value={s.location_label || "Creative Mojo HQ"} />
          <ShadowRow icon={FileText} label="Meeting with" value={s.host?.name || "Sandra Caldeira-Dunkerley"} />
          {s.host?.email && (
            <ShadowRow icon={Mail} label="Email" value={<a className="text-stone-950 underline" href={`mailto:${s.host.email}`}>{s.host.email}</a>} />
          )}
          {s.host?.mobile && (
            <ShadowRow icon={Phone} label="Mobile" value={<a className="text-stone-950 underline" href={`tel:${s.host.mobile.replace(/\s+/g, "")}`}>{s.host.mobile}</a>} last />
          )}
        </div>
      )}

      {s.status === "regional" && (
        <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm max-w-xl" data-testid="discovery-shadow-regional">
          <ShadowRow icon={Calendar} label="Shadow Day Date" value={dateFormatted} />
          <ShadowRow icon={FileText} label="Regional Franchisee" value={s.franchisee?.name} />
          {s.franchisee?.mobile && (
            <ShadowRow icon={Phone} label="Mobile" value={<a className="text-stone-950 underline" href={`tel:${s.franchisee.mobile.replace(/\s+/g, "")}`}>{s.franchisee.mobile}</a>} last />
          )}
        </div>
      )}
    </div>
  );
}

function ShadowRow({ icon: Icon, label, value, last }) {
  return (
    <div className={`flex items-start gap-3 py-3 ${last ? "" : "border-b border-stone-100"}`}>
      <Icon className="w-4 h-4 mt-0.5 text-stone-400 shrink-0" />
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-stone-500">{label}</div>
        <div className="text-sm text-stone-900 mt-0.5">{value || <span className="text-stone-400">—</span>}</div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------
// Training Days panel — mirrors the shadow-day pattern. Only rendered
// when the Sales Pipeline checklist has any training info populated
// (dates or location); otherwise the section is hidden entirely.
// -----------------------------------------------------------------
function TrainingDaysPanel({ training_days }) {
  const t = training_days || { status: "pending" };
  const dates = Array.isArray(t.dates) ? t.dates : [];
  const formatted = dates.map((d) => {
    try {
      return new Date(d).toLocaleDateString("en-GB", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    } catch { return d; }
  });
  return (
    <div>
      <p className="text-stone-700 leading-relaxed mb-6 max-w-3xl">
        If you have arranged your initial training with head office, the information for this will appear
        below for your confirmation.
      </p>

      {t.status === "pending" && (
        <Placeholder text="Awaiting Training Day(s) Appointment" testId="discovery-training-pending" />
      )}

      {t.status === "booked" && Array.isArray(t.sessions) && t.sessions.length > 0 && (
        <div className="space-y-3 max-w-xl" data-testid="discovery-training-details">
          {t.sessions.map((s, i) => (
            <div key={s.date} className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid={`discovery-training-session-${i}`}>
              <ShadowRow icon={Calendar} label={t.sessions.length > 1 ? `Training Day ${i + 1}` : "Training Date"}
                value={<span data-testid={`discovery-training-date-${i}`}>{formatted[dates.indexOf(s.date)] || s.date}</span>} />
              {s.trainer_name && (
                <ShadowRow icon={FileText} label="Trainer" value={<span data-testid={`discovery-training-trainer-${i}`}>{s.trainer_name}</span>} />
              )}
              <ShadowRow icon={MapPin} label="Location"
                value={s.location ? <span data-testid={`discovery-training-location-${i}`}>{s.location}</span> : ""} last />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------
// Password screen — shown when the portal exists and is active but
// this browser hasn't yet authenticated. A single shared password
// (server-side env var) protects every portal; a successful entry
// sets a 30-day HttpOnly ``dp_pw`` cookie on this device so the
// visitor never sees this screen again until the password rotates
// or the cookie expires.
// -----------------------------------------------------------------
function PasswordScreen({ firstName, slug, onSuccess }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!password || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await publicApi.post("/public/discovery/verify-password", {
        slug, password,
      });
      onSuccess?.();
    } catch (err) {
      // Same error text regardless of underlying cause (bad password
      // vs. missing slug etc.) so we never confirm slug existence to
      // someone still on the password screen.
      setError("That password isn't right. Please double-check and try again.");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-stone-50 flex items-center justify-center px-6"
      data-testid="discovery-password-screen"
    >
      <div className="max-w-md w-full text-center">
        <img src={LOGO} alt="Creative Mojo" className="h-[4.5rem] mx-auto mb-8" />
        <h1 className="text-2xl font-bold text-stone-900">
          {firstName ? `Welcome, ${firstName}` : "Welcome"}
        </h1>
        <p className="mt-3 text-stone-600 leading-relaxed">
          Please enter the password we&apos;ve shared with you to open your Discovery Portal.
          You&apos;ll only need to do this once on this device.
        </p>
        <form onSubmit={submit} className="mt-8 text-left">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-2">
            Password
          </label>
          <input
            type="password"
            autoFocus
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="discovery-password-input"
            className="w-full px-4 py-3 bg-white border border-stone-300 rounded-lg text-base focus:outline-none focus:border-stone-950"
            placeholder="Enter password"
            disabled={submitting}
          />
          {error && (
            <div
              className="mt-3 text-sm text-red-600"
              data-testid="discovery-password-error"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            data-testid="discovery-password-submit"
            disabled={submitting || !password}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-stone-950 text-[#dddd16] font-bold uppercase tracking-wider text-sm rounded-lg hover:bg-stone-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Unlocking…</>) : "Open Discovery Portal"}
          </button>
        </form>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------
// Main page
// -----------------------------------------------------------------
export default function DiscoveryPortalPage() {
  const { slug: slugFromRoute } = useParams();
  const slug = (slugFromRoute || "").trim();

  const tokenFromUrl = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get("t") || "").trim();
    } catch { return ""; }
  }, []);

  const [state, setState] = useState({ loading: true, data: null, error: null });

  // ``state.error`` values:
  //   • ``not_found``       — portal missing / inactive → show not-found screen
  //   • ``password_required`` — portal exists → show password form
  //   • ``null``            — payload available in ``state.data``
  const [passwordFirstName, setPasswordFirstName] = useState("");

  const loadPortal = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) setState((s) => ({ ...s, loading: true }));
    try {
      // ---- Personalised URL bootstrap ----
      // ``/discovery/<slug>?t=<token>`` — establish the legacy
      // ``dp_<slug>`` session cookie once, then strip the token from
      // the address bar BEFORE we render anything sensitive.
      if (slug && tokenFromUrl) {
        try {
          await publicApi.post("/public/discovery/session", {
            slug, token: tokenFromUrl,
          });
          try {
            window.history.replaceState({}, "", `/discovery/${slug}`);
          } catch { /* best-effort */ }
        } catch {
          // Fall through — if the token was invalid the load below
          // will surface either password_required or not_found.
        }
      }

      const params = slug
        ? { slug }
        : (tokenFromUrl ? { t: tokenFromUrl } : null);
      if (!params) {
        setState({ loading: false, data: null, error: "not_found" });
        return;
      }
      const { data } = await publicApi.get("/public/discovery", { params });

      // Legacy bare-token promotion — same behaviour as before.
      if (!slug && tokenFromUrl && data?.slug) {
        try {
          await publicApi.post("/public/discovery/session", {
            slug: data.slug, token: tokenFromUrl,
          });
        } catch { /* non-fatal */ }
        try {
          window.history.replaceState({}, "", `/discovery/${data.slug}`);
        } catch { /* best-effort */ }
      }
      setState({ loading: false, data, error: null });
    } catch (e) {
      // 401 with error=password_required → show the password screen
      // (the portal DOES exist and is active; the visitor just needs
      // to authenticate on this device).
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401 && detail?.error === "password_required") {
        setPasswordFirstName((detail.first_name || "").trim());
        setState({ loading: false, data: null, error: "password_required" });
        return;
      }
      setState({ loading: false, data: null, error: "not_found" });
    }
  }, [slug, tokenFromUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadPortal();
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [loadPortal]);

  // Auth query-string appended to same-origin PDF stream URLs.
  // Prefers slug (cookie flow) once the session has been
  // established; falls back to the query-string token for the
  // legacy bootstrap link before promotion has happened.
  const effectiveSlug = slug || state.data?.slug || "";
  const authQuery = effectiveSlug
    ? `slug=${encodeURIComponent(effectiveSlug)}`
    : `t=${encodeURIComponent(tokenFromUrl)}`;

  // Loading state
  if (state.loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-stone-500" />
      </div>
    );
  }

  // Password-required state — portal exists and is active but this
  // browser hasn't authenticated yet (or the shared password has been
  // rotated since the last visit). Once the visitor enters the
  // password correctly, the server sets a 30-day HttpOnly cookie so
  // subsequent visits are unlocked automatically.
  if (state.error === "password_required") {
    return (
      <PasswordScreen
        firstName={passwordFirstName}
        slug={slug}
        onSuccess={() => loadPortal({ silent: true })}
      />
    );
  }

  // Not-found state — identical for missing / inactive portals so
  // the endpoint doesn't leak which slugs exist.
  if (state.error === "not_found") {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6" data-testid="discovery-not-found">
        <div className="max-w-md text-center">
          <img src={LOGO} alt="Creative Mojo" className="h-[4.5rem] mx-auto mb-8 opacity-70" />
          <h1 className="text-2xl font-bold text-stone-900">Discovery Portal not found</h1>
          <p className="mt-3 text-stone-600 leading-relaxed">
            The link you followed doesn&apos;t lead to an active Discovery Portal. If you were expecting to see
            information about a Creative Mojo franchise, please contact Paul or Sandra and they&apos;ll issue a
            fresh link.
          </p>
        </div>
      </div>
    );
  }

  const { first_name, territory, shadow_day, training_days, agreement, show_flex, launch_portal, confidentiality_agreement } = state.data;

  return (
    <DbsDirtyGate>
      <DiscoveryPortalLayout
        data={state.data}
        authQuery={authQuery}
        first_name={first_name}
        territory={territory}
        shadow_day={shadow_day}
        training_days={training_days}
        agreement={agreement}
        show_flex={show_flex}
        launch_portal={launch_portal}
        confidentiality_agreement={confidentiality_agreement}
        slug={slug}
        reload={loadPortal}
      />
    </DbsDirtyGate>
  );
}

// -----------------------------------------------------------------
// Portal layout — routes rendering by breakpoint.
//
// * Desktop (≥ md): fixed left sidebar + one-section-at-a-time
//   content panel.  Selection is driven purely by sidebar clicks,
//   never by scroll position, so the previous scroll-spy race
//   (highlight lagging the visible section) is retired.
// * Mobile: the pre-existing "single long page + sticky top strip"
//   layout is retained unchanged.
// -----------------------------------------------------------------
function DiscoveryPortalLayout({ authQuery, first_name, territory, shadow_day, training_days, agreement, show_flex, launch_portal, confidentiality_agreement, slug, reload }) {
  const [desktopActive, setDesktopActive] = useState(NAV_SECTIONS[0].id);
  // Feb-2026 owner directive: mobile now shows one selected section at
  // a time (no more stacked long-scroll). ``mobileActive`` drives the
  // renderer AND highlights the tapped nav item. Kept separate from
  // ``desktopActive`` so the two viewport variants don't fight if the
  // user resizes while a section is open — each remembers its own last
  // selection.
  const [mobileActive, setMobileActive] = useState(NAV_SECTIONS[0].id);
  const isDesktop = useIsDesktop();

  // Whenever the mobile section changes, jump the window to the top
  // so the new section starts at its heading. ``behavior: "auto"``
  // (per brief: "Do not animate or scroll past other sections").
  useEffect(() => {
    if (isDesktop) return;
    if (typeof window === "undefined") return;
    // Defer to next tick so the section DOM node is mounted first.
    const t = setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 0);
    return () => clearTimeout(t);
  }, [mobileActive, isDesktop]);

  // ------------------------------------------------------------------
  // Launch Portal <-> Discovery Portal mobile nav swap (Feb 2026).
  //
  // When a franchisee has been finalised, the same URL renders BOTH
  // portals stacked one above the other. The sticky Discovery nav
  // strip previously stayed pinned to the viewport even after the
  // user scrolled past the Discovery section, which made no sense
  // for anyone who is really only here for the Launch Portal.
  //
  //  * ``lpActive`` lifts the Launch Portal's active-section state up
  //    so BOTH copies of the nav (the inline one inside the overlay
  //    and the sticky one we render in the Discovery header below)
  //    share a single source of truth.
  //  * ``insideLaunchPortal`` flips to true the moment the Launch
  //    Portal section reaches the top of the viewport, at which
  //    point we hide the Discovery mobile nav and swap in the
  //    Launch Portal one — an in-place replacement, not a stack.
  //  * ``hasLaunchPortal`` gates all of the above; on prospects who
  //    haven't been finalised yet the page renders exactly as before.
  // ------------------------------------------------------------------
  const hasLaunchPortal = !!(launch_portal && launch_portal.finalised_at);
  const lpSections = useLaunchPortalSections(hasLaunchPortal ? launch_portal : null);
  const [lpActive, setLpActive] = useState(lpSections[0]?.id || "contract");
  const [insideLaunchPortal, setInsideLaunchPortal] = useState(false);
  // Feb-2026 owner directive — once the Launch Portal is live, the
  // Discovery section is a "reference" surface, not the primary one.
  // Collapse it by default behind a single "Discovery" chevron toggle,
  // leaving the Launch nav / content as the primary navigation. Users
  // can still re-open it any time to revisit forms, maps, agreements.
  // All child state (``mobileActive``, ``desktopActive``, any inline
  // form state inside the Discovery sections) is preserved while
  // collapsed because the subtree is simply hidden, never unmounted
  // for prospects without a finalised Launch Portal (hasLaunchPortal
  // = false) — those visitors keep the current always-expanded layout.
  const [discoveryExpanded, setDiscoveryExpanded] = useState(!hasLaunchPortal);

  // Unsaved-progress gate (Feb-2026 — Sam). Every nav action that
  // would unmount the Launch Portal's DBS section funnels through
  // ``requestLeave`` so the user is prompted before losing in-flight
  // DBS form entries. When the DBS form is untouched the gate's
  // ``requestLeave`` is a straight pass-through (no dialog), so these
  // wrappers add zero overhead to the common path.
  const dbsGate = useDbsDirtyGate();
  const gatedSetDesktopActive = (id) => dbsGate.requestLeave(() => setDesktopActive(id));
  const gatedSetMobileActive = (id) => dbsGate.requestLeave(() => setMobileActive(id));
  const gatedSetLpActive = (id) => dbsGate.requestLeave(() => setLpActive(id));
  const gatedToggleDiscovery = () => dbsGate.requestLeave(() => setDiscoveryExpanded((v) => !v));

  // Track when the Launch Portal overlay is at (or above) the top of
  // the viewport. Using ``getBoundingClientRect().top`` against a
  // small offset that matches the sticky header height keeps the
  // swap-in feeling immediate on mobile without the flicker an
  // ``IntersectionObserver`` can produce at boundaries.
  useEffect(() => {
    if (!hasLaunchPortal) {
      setInsideLaunchPortal(false);
      return undefined;
    }
    const HEADER_OFFSET_PX = 96; // approx height of the title/logo bar
    const onScroll = () => {
      const el = document.querySelector('[data-testid="launch-portal-overlay"]');
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setInsideLaunchPortal(top <= HEADER_OFFSET_PX);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Run once so a page that loads already scrolled (or the mount
    // auto-scroll below) picks up the correct initial state.
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasLaunchPortal]);

  // Feb-2026 — the mobile first-load auto-scroll to the Launch Portal
  // overlay has been retired. With the collapsible Discovery heading
  // (default: collapsed when a Launch Portal is live), the Launch
  // Portal already sits immediately below a single-line toggle at the
  // top of the page, so there is nothing to scroll past. Preserving
  // the effect would also fire a jump-down the moment a user chose to
  // expand Discovery, which was the opposite of what they asked for.

  const introBlock = (
    <>
      <p className="text-xl font-bold text-stone-900">Hello {first_name},</p>
      <p className="mt-4 text-stone-700 leading-relaxed max-w-4xl">
        Following our recent conversation, we&apos;ve created this personal Discovery Portal for you. Here you&apos;ll
        find everything relating to your proposed Creative Mojo franchise, including your territory, example
        agreement, marketing materials and any upcoming shadow or training dates.
      </p>
      <p className="mt-4 text-stone-700 leading-relaxed max-w-4xl">
        As you progress through the process, this page will continue to evolve with you. It is here to
        provide you with all the information you need, whenever you need it.
      </p>
    </>
  );

  // ``renderSection`` returns the JSX for a given nav id.  Reused
  // by both breakpoints so the section components / data fetches
  // are never duplicated.
  const renderSection = (id) => {
    switch (id) {
      case "territory":
        return territory && (territory.sectors?.length || 0) > 0 ? (
          <TerritoryPanel territory={territory} />
        ) : (
          <Placeholder text="Awaiting Territory Generation" testId="discovery-territory-pending" />
        );
      case "agreement":
        return agreement ? (
          <AgreementPanel
            authQuery={authQuery}
            showFlex={!!show_flex}
          />
        ) : (
          <div>
            <p className="text-stone-700 leading-relaxed mb-6 max-w-3xl">
              An example Franchise Agreement will be available here shortly. In the meantime, please contact
              Paul or Sandra if you would like a preview.
            </p>
            <Placeholder text="Sample Agreement Coming Shortly" testId="discovery-agreement-pending" />
          </div>
        );
      case "shadow":
        return <ShadowDayPanel shadow_day={shadow_day} />;
      case "confidentiality":
        return <ConfidentialityAgreementPanel data={confidentiality_agreement} slug={slug} reload={reload} />;
      case "training":
        return <TrainingDaysPanel training_days={training_days} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="discovery-portal-page">
      {/* Sticky top header — always visible on both breakpoints. */}
      <header
        className="sticky top-0 z-40 bg-white border-b border-stone-200"
        data-testid="discovery-sticky-header"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 sm:py-6 flex items-center justify-between gap-4">
          <h1
            className="text-2xl sm:text-3xl font-black tracking-tight text-stone-900"
            data-testid="discovery-page-title"
          >
            Discover Creative Mojo
          </h1>
          <img
            src={LOGO}
            alt="Creative Mojo"
            className="h-[3.125rem] sm:h-[4.375rem] shrink-0"
          />
        </div>
        {/* Discovery expand/collapse toggle — pinned INSIDE the sticky
            header when a Launch Portal is live so franchisees can
            re-open Discovery from anywhere on the page, and so the
            chevron always sits ABOVE the Discovery mobile nav strip
            (which lives directly below). Only rendered when a Launch
            Portal is live; prospects without one still see the plain
            "Your Discovery Portal" heading in the content column. */}
        {hasLaunchPortal && (
          <div className="max-w-6xl mx-auto px-6 sm:px-10 pb-3 md:pb-4">
            <button
              type="button"
              onClick={gatedToggleDiscovery}
              aria-expanded={discoveryExpanded}
              aria-controls="discovery-portal-content"
              data-testid="discovery-portal-collapse-toggle"
              className="w-full flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 text-left hover:border-stone-300 transition-colors"
            >
              <span className="flex items-center gap-2 min-w-0">
                <Search className="w-5 h-5 text-stone-950 shrink-0" />
                <span
                  className="font-display text-xl md:text-2xl text-stone-950 truncate"
                  data-testid="discovery-portal-overlay-heading"
                >
                  Discovery
                </span>
              </span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">
                  {discoveryExpanded ? "Hide" : "Show"}
                </span>
                {discoveryExpanded ? (
                  <ChevronUp
                    className="w-5 h-5 text-stone-700"
                    data-testid="discovery-portal-collapse-icon-up"
                    aria-hidden="true"
                  />
                ) : (
                  <ChevronDown
                    className="w-5 h-5 text-stone-700"
                    data-testid="discovery-portal-collapse-icon-down"
                    aria-hidden="true"
                  />
                )}
              </span>
            </button>
          </div>
        )}
        {/* Mobile-only sticky nav slot (``md:hidden`` guarantees it
            never paints on desktop / tablet).
            Three states:
              • Discovery collapsed (default when a Launch Portal is
                live): render NOTHING. The inline Launch nav attached
                to the "Your Launch Portal" section below is the only
                set of icons the user sees, so there's no doubling-up
                in the sticky header.
              • Discovery expanded, viewer still at the Discovery
                section: render the Discovery mobile nav so section
                switching is reachable without scrolling.
              • Discovery expanded, viewer scrolled past Discovery
                into the Launch Portal: swap in the Launch mobile
                strip so Launch section switching stays reachable
                while scrolled. Both copies share ``lpActive`` state. */}
        {hasLaunchPortal && !discoveryExpanded ? null : hasLaunchPortal && insideLaunchPortal ? (
          <div className="md:hidden">
            <LaunchPortalMobileNavStrip
              sections={lpSections}
              active={lpActive}
              setActive={gatedSetLpActive}
              testId="launch-portal-sticky-nav"
            />
          </div>
        ) : (
          <DiscoveryMobileNav active={mobileActive} setActive={gatedSetMobileActive} />
        )}
      </header>

      {/* Sub-heading above the Discovery content — only rendered for
          prospects WITHOUT a Launch Portal (the finalised-launch flow
          renders the "Discovery" chevron toggle inside the sticky
          header above instead). */}
      {!hasLaunchPortal && (
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-8 md:pt-10">
          <header
            className="flex items-center gap-2 mb-2 md:mb-4"
            data-testid="discovery-portal-overlay-heading"
          >
            <Search className="w-5 h-5 text-stone-950" />
            <h2 className="font-display text-xl md:text-2xl text-stone-950">
              Your Discovery Portal
            </h2>
          </header>
        </div>
      )}

      {(!hasLaunchPortal || discoveryExpanded) && (
      <div id="discovery-portal-content" data-testid="discovery-portal-content">
      {isDesktop ? (
        /* ============================== DESKTOP =========================
           Fixed left nav + selected-section panel.  Nothing scrolls the
           whole page from the sidebar — only the section panel scrolls
           when its own content is taller than the viewport. */
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pb-10 grid grid-cols-[220px_1fr] gap-10">
          <DiscoveryDesktopSidebar
            active={desktopActive}
            onSelect={gatedSetDesktopActive}
          />
          <main data-testid="discovery-desktop-panel" className="min-w-0">
            {desktopActive === "__home__" ? (
              <section data-testid="discovery-introduction">
                {introBlock}
              </section>
            ) : (
              (() => {
                const cfg = NAV_SECTIONS.find((s) => s.id === desktopActive);
                if (!cfg) return null;
                return (
                  <section data-testid={`discovery-${desktopActive}-section`}>
                    <SectionHeading
                      testId={`discovery-${desktopActive}-section-heading`}
                      icon={cfg.icon}
                      tone={cfg.tone}
                    >
                      {cfg.label}
                    </SectionHeading>
                    {renderSection(desktopActive)}
                  </section>
                );
              })()
            )}
            <footer className="mt-16 pt-8 border-t border-stone-200 text-xs text-stone-400 text-center">
              Creative Mojo · Your Discovery Portal
            </footer>
          </main>
        </div>
      ) : (
        /* ============================== MOBILE ==========================
           Feb-2026 owner directive: show ONE selected section at a time.
           No more one-long-page stacking, no smooth-scroll past other
           sections — the nav simply switches state and the panel below
           starts at its heading. Users can still scroll normally inside
           the selected section for long content (e.g. Territory map,
           Confidentiality Agreement form). */
        <div className="max-w-6xl mx-auto px-6 pb-8" data-testid="discovery-mobile-panel">
          {mobileActive === "__home__" ? (
            <section data-testid="discovery-introduction">
              <MobileViewingAdviceBanner testId="discovery-mobile-viewing-advice-home" />
              {introBlock}
            </section>
          ) : (
            (() => {
              const cfg = NAV_SECTIONS.find((s) => s.id === mobileActive);
              if (!cfg) return null;
              return (
                <Section
                  id={cfg.id}
                  heading={cfg.label}
                  icon={cfg.icon}
                  tone={cfg.tone}
                  testId={`discovery-${cfg.id}-section`}
                  bannerTestId={`discovery-mobile-viewing-advice-${cfg.id}`}
                  showMobileBanner
                >
                  {renderSection(cfg.id)}
                </Section>
              );
            })()
          )}
          <footer className="mt-16 pt-8 border-t border-stone-200 text-xs text-stone-400 text-center">
            Creative Mojo · Your Discovery Portal
          </footer>
        </div>
      )}
      </div>
      )}
      {launch_portal && launch_portal.finalised_at && (
        <LaunchPortalOverlay
          launchPortal={launch_portal}
          slug={slug}
          reload={reload}
          active={lpActive}
          setActive={gatedSetLpActive}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Mobile viewing-advice banner (Feb-2026 owner brief).
// A small, readable banner shown at the top of every section on
// mobile (both portals). Reassures prospects the portal can be
// used on a phone while nudging them to switch to a bigger screen
// when possible. Hidden at md+ via ``md:hidden``.
// Exported so LaunchPortalOverlay can render the exact same banner.
// ---------------------------------------------------------------
export function MobileViewingAdviceBanner({ testId = "mobile-viewing-advice" }) {
  return (
    <div
      className="md:hidden mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-900"
      role="status"
      data-testid={testId}
    >
      <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" aria-hidden="true" />
      <span>
        Although you can use this portal on a mobile device, it is best viewed on a tablet or desktop computer.
      </span>
    </div>
  );
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(min-width: 768px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isDesktop;
}

// ---------------------------------------------------------------
// Navigation helpers — kept below the main page component so the
// two viewport variants share a single active-section tracker.
// ---------------------------------------------------------------
function useActiveSection() {
  const [active, setActive] = useState(NAV_SECTIONS[0].id);
  useEffect(() => {
    const targets = NAV_SECTIONS
      .filter((s) => s.id !== "__home__")     // Home has no anchor node
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);

    // Treat "close to the top of the page" as the Home slot being
    // active so the Home button lights up until the user scrolls
    // into the first real section.
    const HOME_THRESHOLD_PX = 120;
    const onScroll = () => {
      if (window.scrollY < HOME_THRESHOLD_PX) setActive("__home__");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    if (!targets.length) return () => window.removeEventListener("scroll", onScroll);
    const io = new IntersectionObserver(
      (entries) => {
        if (window.scrollY < HOME_THRESHOLD_PX) return;   // Home wins near top
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0.01 },
    );
    for (const el of targets) io.observe(el);
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);
  return active;
}

function scrollToSection(id) {
  if (id === "__home__") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function DiscoveryDesktopSidebar({ active, onSelect }) {
  return (
    <aside
      className="relative z-10"
      data-testid="discovery-sidebar"
    >
      <div className="sticky top-32 space-y-2">
        {NAV_SECTIONS.map((s) => {
          const Icon = s.icon;
          const isActive = active === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelect(s.id)}
              data-testid={`discovery-nav-desktop-${s.id}`}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors ring-1 cursor-pointer ${
                isActive
                  ? `${s.tone.activeBg} ${s.tone.activeRing}`
                  : `bg-white ring-stone-200 hover:${s.tone.bg}`
              }`}
            >
              <span
                className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${s.tone.bg} ${s.tone.text} shrink-0`}
                aria-hidden="true"
              >
                <Icon className="w-5 h-5" />
              </span>
              <span className={`text-[11px] font-bold uppercase tracking-wider leading-tight ${isActive ? "text-stone-900" : "text-stone-700"}`}>
                {s.label}
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}

function DiscoveryMobileNav({ active, setActive }) {
  return (
    <nav
      className="md:hidden border-t border-stone-100 bg-white"
      data-testid="discovery-mobile-nav"
    >
      <div className="max-w-6xl mx-auto px-3 pt-2 pb-3">
        <p className="text-[10px] uppercase tracking-wider text-stone-500 text-center mb-1.5">
          Navigate the Portal using the buttons below
        </p>
        <div className="grid grid-cols-5 gap-1.5">
          {NAV_SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                data-testid={`discovery-nav-mobile-${s.id}`}
                className={`flex flex-col items-center gap-1 px-1 py-2 rounded-lg ring-1 transition-colors ${
                  isActive
                    ? `${s.tone.activeBg} ${s.tone.activeRing}`
                    : `${s.tone.bg} ring-stone-200`
                }`}
              >
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full bg-white ${s.tone.text}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <span className={`text-[8.5px] font-bold uppercase tracking-wider leading-tight text-center ${isActive ? "text-stone-900" : "text-stone-600"}`}>
                  {s.shortLabel}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
